import { useState } from 'react';
import { useAtomValue } from 'jotai';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import LanguageIcon from '@mui/icons-material/Language';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { applyLanguage } from '@/lib/i18n';
import { useLanguages } from '@/hooks/useLanguages';
import { currentLangAtom } from '@/store/langAtom';
import { useContent } from '@/hooks/useContent';

export type LanguageSwitcherVariant = 'outlined' | 'icon' | 'sidebar';

interface LanguageSwitcherProps {
  /**
   * `outlined` — compact labeled dropdown (public headers).
   * `icon` — globe Language IconButton for compact mobile public-card headers.
   * `sidebar` — Figma Select with floating Language label (AppLayout rail).
   */
  variant?: LanguageSwitcherVariant;
}

export default function LanguageSwitcher({ variant = 'outlined' }: Readonly<LanguageSwitcherProps>) {
  const activeLang = useAtomValue(currentLangAtom);
  const { languages, isLoading } = useLanguages();
  const { getContentString } = useContent();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isActive = (code: string) => activeLang === code;

  const current = languages.find(l => isActive(l.appLanguageCode));
  const currentLabel = current?.description ?? activeLang;
  const languageLabel = getContentString('PPNavLanguage') || 'Language';

  if (isLoading) {
    if (variant === 'icon') return <Skeleton variant="circular" width={40} height={40} />;
    if (variant === 'sidebar') return <Skeleton variant="rounded" width="100%" height={56} />;
    return <Skeleton variant="rounded" width="100%" height={34} />;
  }

  const menu = (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      slotProps={{
        paper: { sx: { borderRadius: variant === 'sidebar' ? 2 : 3, minWidth: 160, mt: 0.5 } },
      }}
    >
      {languages.map(({ appLanguageCode, description }) => {
        const active = isActive(appLanguageCode);
        return (
          <MenuItem
            key={appLanguageCode}
            selected={active}
            onClick={() => { applyLanguage(appLanguageCode); setAnchorEl(null); }}
            sx={{ fontSize: variant === 'sidebar' ? '0.875rem' : '0.8125rem' }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {active && <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
            </ListItemIcon>
            <ListItemText>
              <Box
                component="span"
                dir="auto"
                sx={{
                  fontWeight: active ? 600 : 400,
                  color: active ? 'primary.main' : undefined,
                }}
              >
                {description}
              </Box>
            </ListItemText>
          </MenuItem>
        );
      })}
    </Menu>
  );

  if (variant === 'icon') {
    return (
      <>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={`${languageLabel}: ${currentLabel}`}
          color="primary"
          size="medium"
          sx={{ p: 1 }}
        >
          <LanguageIcon />
        </IconButton>
        {menu}
      </>
    );
  }

  if (variant === 'sidebar') {
    // Figma Sidebar Select: floating "Language" label + translate adornment + value + caret.
    return (
      <>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <Box
            component="span"
            id="pp-language-switcher-label"
            sx={{
              position: 'absolute',
              top: -1,
              left: 11,
              zIndex: 1,
              px: 0.5,
              bgcolor: 'background.paper',
              fontSize: 12,
              lineHeight: '15px',
              letterSpacing: '0.15px',
              color: 'text.secondary',
              pointerEvents: 'none',
            }}
          >
            {languageLabel}
          </Box>
          <Button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-labelledby="pp-language-switcher-label"
            aria-label={`${languageLabel}: ${currentLabel}`}
            variant="outlined"
            color="inherit"
            startIcon={<TranslateOutlinedIcon sx={{ fontSize: 24, color: 'text.secondary' }} />}
            endIcon={
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 24,
                  color: 'text.secondary',
                  transition: 'transform 150ms',
                  transform: open ? 'rotate(180deg)' : 'none',
                }}
              />
            }
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 400,
              letterSpacing: '0.15px',
              lineHeight: '24px',
              color: 'text.primary',
              borderColor: 'rgba(0,0,0,0.23)',
              borderRadius: '4px',
              px: 1.5,
              py: 2,
              bgcolor: 'background.paper',
              '& .MuiButton-startIcon': { mr: 1 },
              '& .MuiButton-endIcon': { ml: 'auto' },
              '&:hover': {
                borderColor: 'text.primary',
                bgcolor: 'background.paper',
              },
            }}
          >
            <Box component="span" dir="auto" sx={{ flex: 1, textAlign: 'start', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentLabel}
            </Box>
          </Button>
        </Box>
        {menu}
      </>
    );
  }

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`${languageLabel}: ${currentLabel}`}
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<LanguageIcon sx={{ fontSize: 16 }} />}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 16,
              transition: 'transform 150ms',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        }
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          textTransform: 'none',
          fontSize: '0.75rem',
          fontWeight: 500,
          color: 'text.secondary',
          borderColor: 'divider',
          borderRadius: 2,
          px: 1.5,
          py: 0.75,
          '&:hover': {
            borderColor: 'action.disabled',
            bgcolor: 'action.hover',
          },
        }}
      >
        <span dir="auto" style={{ flex: 1, textAlign: 'start' }}>
          {currentLabel}
        </span>
      </Button>
      {menu}
    </>
  );
}
